// Small map badges drawn once onto a canvas and shared as textures: the "S"
// sale plaque, owners' round avatars (with the gold VIP ring), the fallback
// letter, and the lighthouse glow.
//
// The profiler (tools/profiling) showed what they used to cost: one PIXI.Text
// texture per plaque and letter, a stencil mask per avatar (109 masks — each a
// stencil write plus a broken batch), and a BlurFilter re-rendered every frame
// for the glow. A shared texture is one sprite in the normal sprite batch.
import * as PIXI from 'pixi.js';

/** Pixels per world unit in the badge canvases: sharp when zoomed in, mipmapped when zoomed out. */
const RES = 4;

const cache = new Map<string, PIXI.Texture>();

function canvasTexture(key: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): PIXI.Texture {
  const hit = cache.get(key);
  if (hit && !hit.destroyed) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w * RES);
  canvas.height = Math.ceil(h * RES);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(RES, RES);
  draw(ctx);
  const texture = PIXI.Texture.from(canvas);
  texture.source.autoGenerateMipmaps = true;
  texture.source.scaleMode = 'linear';
  cache.set(key, texture);
  return texture;
}

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

/** A rounded-rectangle path (ctx.roundRect is iOS 16+ only). */
function roundedRect(ctx: CanvasRenderingContext2D, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
}

// ── Sale plaque ──

const SALE_FONT_SIZE = 26;
const SALE_PAD_X = 12;
const SALE_PAD_Y = 5;
const SALE_RADIUS = 8;

/** The plaque over a house for sale — world size of the sprite is width/height of the result. */
export function saleTagTexture(label: string, bgColor: number): { texture: PIXI.Texture; width: number; height: number } {
  const font = `bold ${SALE_FONT_SIZE}px Arial, sans-serif`;
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = font;
  const textW = measure.measureText(label).width;
  const textH = Math.ceil(SALE_FONT_SIZE * 1.17);
  const width = Math.ceil(textW + SALE_PAD_X * 2);
  const height = textH + SALE_PAD_Y * 2;
  const texture = canvasTexture(`sale|${label}|${bgColor}`, width, height, (ctx) => {
    ctx.globalAlpha = 0.94;
    ctx.fillStyle = hex(bgColor);
    roundedRect(ctx, width, height, SALE_RADIUS);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetX = Math.cos(Math.PI / 4);
    ctx.shadowOffsetY = Math.sin(Math.PI / 4);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, width / 2, height / 2 + 1);
  });
  return { texture, width, height };
}

// ── Owner avatars ──

export const AVATAR_SIZE = 28;
const AVATAR_VIP_BORDER = 2;
/** World size of an avatar badge (the VIP ring included). */
export const AVATAR_TOTAL = AVATAR_SIZE + AVATAR_VIP_BORDER * 2;
const AVATAR_FALLBACK_BG = '#2a9d8f';
const VIP_RING = '#ef9b03';

function avatarBadge(key: string, vip: boolean, drawFace: (ctx: CanvasRenderingContext2D, cx: number, r: number) => void): PIXI.Texture {
  return canvasTexture(key, AVATAR_TOTAL, AVATAR_TOTAL, (ctx) => {
    const c = AVATAR_TOTAL / 2;
    if (vip) {
      ctx.fillStyle = VIP_RING;
      ctx.beginPath();
      ctx.arc(c, c, AVATAR_TOTAL / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, AVATAR_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    drawFace(ctx, c, AVATAR_SIZE / 2);
    ctx.restore();
  });
}

/** The first letter on teal, shown until the photo arrives (or when there is none). */
export function avatarLetterTexture(letter: string, vip: boolean): PIXI.Texture {
  return avatarBadge(`avatar-letter|${letter}|${vip}`, vip, (ctx, c, r) => {
    ctx.fillStyle = AVATAR_FALLBACK_BG;
    ctx.fillRect(c - r, c - r, r * 2, r * 2);
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(letter, c, c + 1);
  });
}

/** The owner's photo cut into a circle. `photo` is the loaded avatar texture; null if it cannot be drawn. */
export function avatarPhotoTexture(photoUrl: string, photo: PIXI.Texture, vip: boolean): PIXI.Texture | null {
  const image = photo.source?.resource as CanvasImageSource | undefined;
  if (!image) return null;
  try {
    return avatarBadge(`avatar|${photoUrl}|${vip}`, vip, (ctx, c, r) => {
      ctx.drawImage(image, c - r, c - r, r * 2, r * 2);
    });
  } catch {
    return null;
  }
}

// ── Lighthouse glow ──

/** World diameter of the glow sprite (a blurred r=26 disc reached about this far). */
export const LIGHTHOUSE_GLOW_SIZE = 104;

export function lighthouseGlowTexture(): PIXI.Texture {
  const s = LIGHTHOUSE_GLOW_SIZE;
  return canvasTexture('lighthouse-glow', s, s, (ctx) => {
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(255, 230, 160, 1)');
    g.addColorStop(0.45, 'rgba(255, 230, 160, 0.85)');
    g.addColorStop(0.7, 'rgba(255, 230, 160, 0.3)');
    g.addColorStop(1, 'rgba(255, 230, 160, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
  });
}
